import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

const FULFILLMENT_OPTIONS = [
  { label: 'Ship to Address', value: 'ship_to_address' },
  { label: 'Pick Up Locally', value: 'pick_up_locally' },
  {
    label: 'Ship with Existing Request',
    value: 'ship_with_existing_order',
  },
] as const

type LineItem = {
  product_id: number
  product_name: string
  variant_id: number
  quantity: number
  variant_name?: string
  variant_sku?: string
  variant_format?: string
}

export function MobileSampleReviewStep() {
  const { watch } = useFormContext()

  const companyName = watch('company_name') as string
  const dateNeeded = watch('date_needed') as string
  const contactFirstName = watch('contact_first_name') as string
  const contactLastName = watch('contact_last_name') as string
  const contactPhone = watch('contact_phone') as string
  const shippingAddress1 = watch('shipping_address_line1') as string
  const shippingCity = watch('shipping_city') as string
  const shippingState = watch('shipping_state') as string
  const shippingZip = watch('shipping_zip') as string
  const fulfillmentOption = watch('fulfillment_option') as string

  const fulfillmentOptionLabel = useMemo(() => {
    const opt = FULFILLMENT_OPTIONS.find((o) => o.value === fulfillmentOption)
    return opt?.label ?? fulfillmentOption ?? '-'
  }, [fulfillmentOption])

  const lineItems = (watch('line_items') ?? []) as LineItem[]

  const productGroups = useMemo(() => {
    const groups: Record<number, { productName: string; items: LineItem[] }> =
      {}

    lineItems.forEach((item) => {
      const id = item.product_id
      if (!groups[id]) {
        groups[id] = {
          productName: item.product_name,
          items: [],
        }
      }
      groups[id].items.push(item)
    })

    return Object.values(groups)
  }, [lineItems])

  return (
    <div className="px-4 py-3 space-y-6">
      {/* Request Information */}
      <section className="space-y-3 pb-4">
        <h2 className="text-sm font-semibold text-gray-900">
          Request Information
        </h2>
        <div className="mt-2 h-px bg-gray-200" />

        <div className="space-y-2 text-xs text-gray-800">
          <div className="flex justify-between">
            <span className="font-medium">Date Needed</span>
            <span>{dateNeeded || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">Fulfillment Option</span>
            <span>{fulfillmentOptionLabel || '-'}</span>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="space-y-3 pb-4">
        <h2 className="text-sm font-semibold text-gray-900">
          Contact Information
        </h2>
        <div className="mt-2 h-px bg-gray-200" />

        <div className="space-y-2 text-xs text-gray-800">
          <div className="flex justify-between">
            <span className="font-medium">Company</span>
            <span className="text-green-800 underline">
              {companyName || '-'}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="font-medium">Contact Name</span>
            <span>
              {contactFirstName} {contactLastName}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="font-medium">Contact Phone</span>
            <span>{contactPhone || '-'}</span>
          </div>
        </div>
      </section>

      {/* Shipping Address */}
      <section className="space-y-3 pb-4">
        <h2 className="text-sm font-semibold text-gray-900">
          Shipping Address
        </h2>
        <div className="mt-2 h-px bg-gray-200" />

        <div className="space-y-2 text-xs text-gray-800">
          <div className="flex justify-between">
            <span className="font-medium">Address Line 1</span>
            <span className="text-right">{shippingAddress1 || '-'}</span>
          </div>

          <div className="flex justify-between">
            <span className="font-medium">City</span>
            <span>{shippingCity || '-'}</span>
          </div>

          <div className="flex justify-between">
            <span className="font-medium">State</span>
            <span>{shippingState || '-'}</span>
          </div>

          <div className="flex justify-between">
            <span className="font-medium">Zip Code</span>
            <span>{shippingZip || '-'}</span>
          </div>

          <div className="flex justify-between">
            <span className="font-medium">Country</span>
            <span>US</span>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="space-y-3 pb-4">
        <h2 className="text-sm font-semibold text-gray-900">Products</h2>
        <div className="mt-2 h-px bg-gray-200" />

        {productGroups.length === 0 && (
          <p className="text-xs text-gray-500">No products selected.</p>
        )}

        {productGroups.map((group) => (
          <div
            key={group.productName}
            className="bg-gray-50 px-3 py-2 space-y-2"
          >
            <div className="text-xs font-semibold text-gray-900">
              {group.productName}
            </div>

            {group.items.map((item, idx) => (
              <div
                key={`${item.variant_id}-${idx}`}
                className="mt-2 flex border-l-2 border-gray-300 pl-3 text-xs text-gray-800"
              >
                <div className="space-y-1">
                  {item.variant_sku && (
                    <div className="text-[9px] text-gray-500">
                      SKU: {item.variant_sku}
                    </div>
                  )}
                  <div className="font-medium text-xs">
                    {item.variant_name || 'Variant'}
                  </div>
                  <div className="text-[9px]">Quantity: {item.quantity}</div>
                  {/* {item.variant_format && (
                    <div>Format: {item.variant_format}</div>
                  )} */}
                </div>
              </div>
            ))}
          </div>
        ))}
      </section>
    </div>
  )
}
